// The navigation surface the UI is written against — the Next.js App Router
// API it was built on, implemented over react-router. One module per Next
// module (next/link → @/nav/link, next/navigation → @/nav/navigation,
// next/dynamic → @/nav/dynamic, next's Metadata → @/nav/metadata), so the UI
// split (Phase 3) changed import paths and nothing else, test mocks included.
// New code may use react-router directly; this stays where the two meet.

import {
  Fragment,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  useLocation,
  useMatches,
  useNavigate,
  useParams as useRouterParams,
  useInRouterContext,
  useSearchParams as useRouterSearchParams,
} from "react-router";

import { isHardHref } from "./hrefs";

// ─── refresh(): re-run the current page's loads ─────────────────────────────
// Next's router.refresh() re-rendered server components. In the SPA there are
// none: the equivalent is remounting the page so its effects fetch again.

const RefreshContext = createContext<{ key: number; bump: () => void }>({ key: 0, bump: () => undefined });

export function RefreshProvider({ children }: { children: ReactNode }) {
  const [key, setKey] = useState(0);
  const bump = useCallback(() => setKey((k) => k + 1), []);
  const value = useMemo(() => ({ key, bump }), [key, bump]);
  return <RefreshContext.Provider value={value}>{children}</RefreshContext.Provider>;
}

/** Wraps a page so refresh() remounts it. */
export function RefreshBoundary({ children }: { children: ReactNode }) {
  const { key } = useContext(RefreshContext);
  return <Fragment key={key}>{children}</Fragment>;
}

export interface AppRouter {
  push(href: string, options?: { scroll?: boolean }): void;
  replace(href: string, options?: { scroll?: boolean }): void;
  back(): void;
  forward(): void;
  refresh(): void;
  prefetch(href: string): void;
}

// Outside a router — a component mounted on its own, which Next.js's hooks
// tolerated — the hooks read and move the browser's own location. Whether a
// component sits in a router never changes while it is mounted, so the hook
// order below is stable even though it branches.

const historyRouter: AppRouter = {
  push: (href) => {
    window.history.pushState(null, "", href);
    window.dispatchEvent(new PopStateEvent("popstate"));
  },
  replace: (href) => {
    window.history.replaceState(null, "", href);
    window.dispatchEvent(new PopStateEvent("popstate"));
  },
  back: () => window.history.back(),
  forward: () => window.history.forward(),
  refresh: () => undefined,
  prefetch: () => undefined,
};

export function useRouter(): AppRouter {
  // eslint-disable-next-line react-hooks/rules-of-hooks -- in or out of a router is fixed for a mounted component
  return useInRouterContext() ? useRoutedRouter() : historyRouter;
}

function useRoutedRouter(): AppRouter {
  const navigate = useNavigate();
  const { bump } = useContext(RefreshContext);
  return useMemo<AppRouter>(
    () => ({
      push: (href) => (isHardHref(href) ? window.location.assign(href) : void navigate(href)),
      replace: (href) => (isHardHref(href) ? window.location.replace(href) : void navigate(href, { replace: true })),
      back: () => void navigate(-1),
      forward: () => void navigate(1),
      refresh: bump,
      // Route chunks load on demand; there is nothing to warm.
      prefetch: () => undefined,
    }),
    [navigate, bump],
  );
}

export function usePathname(): string {
  // eslint-disable-next-line react-hooks/rules-of-hooks -- in or out of a router is fixed for a mounted component
  return useInRouterContext() ? useLocation().pathname : window.location.pathname;
}

export function useSearchParams(): URLSearchParams {
  // eslint-disable-next-line react-hooks/rules-of-hooks -- in or out of a router is fixed for a mounted component
  return useInRouterContext() ? useRouterSearchParams()[0] : new URLSearchParams(window.location.search);
}

/** Route metadata the route builder attaches (router/appRoutes.tsx). */
export interface RouteHandle {
  /** The App Router catch-all segment name this route's splat stands for. */
  catchAll?: string;
}

function isRouteHandle(h: unknown): h is RouteHandle {
  return typeof h === "object" && h !== null;
}

export type RouteParams = Record<string, string | string[] | undefined>;

/** App Router params: a catch-all (`[...slug]`, `[[...slug]]`) is a string[]
 *  under its own name, undefined when it matched nothing. */
export function useParams<T extends RouteParams = RouteParams>(): T {
  // eslint-disable-next-line react-hooks/rules-of-hooks -- in or out of a router is fixed for a mounted component
  // No router means no matched route, so no params; T names only optional keys in practice.
  if (!useInRouterContext()) return {} as T;
  // eslint-disable-next-line react-hooks/rules-of-hooks -- in or out of a router is fixed for a mounted component
  return useRoutedParams<T>();
}

function useRoutedParams<T extends RouteParams>(): T {
  const raw = useRouterParams();
  const matches = useMatches();
  return useMemo(() => {
    const out: RouteParams = {};
    for (const [k, v] of Object.entries(raw)) if (k !== "*") out[k] = v;
    for (const m of matches) {
      if (isRouteHandle(m.handle) && m.handle.catchAll) {
        const parts = (raw["*"] ?? "").split("/").filter(Boolean).map(decodeURIComponent);
        out[m.handle.catchAll] = parts.length > 0 ? parts : undefined;
      }
    }
    // The caller names the params its route declares, as in Next.js.
    return out as T;
  }, [raw, matches]);
}

// ─── redirect() / notFound(): thrown, caught by the route error boundary ────

export class RedirectSignal extends Error {
  constructor(readonly href: string) {
    super(`redirect to ${href}`);
    this.name = "RedirectSignal";
  }
}
export class NotFoundSignal extends Error {
  constructor() {
    super("not found");
    this.name = "NotFoundSignal";
  }
}

/** Leave the current page for `href` (rendering stops here). */
export function redirect(href: string): never {
  throw new RedirectSignal(href);
}

/** Render the not-found page instead of this one. */
export function notFound(): never {
  throw new NotFoundSignal();
}

